import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy, Optional, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FormValidationService } from '../services/form-validation.service';
import { controlMessage } from '../domain/editor';

// Row labels across the sheet layouts: .tof-row__label, .rv-row__label, .bms-check__label, .thi-check__item,
// .vrt-td--label, and the card title of a section whose only field is a notes box (.tof-card__title).
const ROW_LABEL = '[class*="__label"], [class*="--label"], [class*="__item"], [class*="__title"]'
// How far up from the control to look for its row; past that the nearest label belongs to another row.
const ROW_DEPTH = 3

const precedes = (el: Element, control: Element) =>
  !el.contains(control) && !!(el.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING)

/**
 * The label of the row a control sits in, as the tech reads it ("Tank Brand:" → "Tank Brand"): in the nearest
 * ancestor that has one, the last labelled element before the control, else the row's leading text cell
 * (VRT's step rows). '' when the row has none.
 */
export function labelFor(control: Element): string {
  let node = control.parentElement
  for (let depth = 0; node && depth < ROW_DEPTH; depth++, node = node.parentElement) {
    const labels = Array.from(node.querySelectorAll(ROW_LABEL)).filter(l => precedes(l, control))
    const lead = node.firstElementChild
    const cell = labels.length ? labels[labels.length - 1] : lead && lead.tagName === 'SPAN' && precedes(lead, control) ? lead : null
    const text = cleanLabel(cell?.textContent ?? '')
    if (text) return text
  }
  return ''
}

function cleanLabel(raw: string): string {
  return raw.replace(/\s+/g, ' ').replace(/[\s:*]+$/, '').trim()
}

/**
 * Renders a red validation message underneath an invalid form control.
 *
 * Applies to every skinned control on the sheet-style forms, so the messages
 * do not have to be hand-written next to each of the hundreds of inputs in the
 * CMC/CME measurement grids. It is inert until FormValidationService.reveal()
 * is called, which BaseFormPage.submit() does when required fields are missing.
 * The same rows name the missing-field chips (key, label).
 *
 * The message is appended to the control's own wrapper rather than replacing
 * anything, and the wrapper gets .rv-has-error so a flex row (e.g. TOF's
 * .tof-row__field) wraps it onto its own line beneath the control instead of
 * beside it — see rv-form.scss.
 *
 * When that immediate wrapper is the wrong place — AVI's Test Result sits in a
 * bordered badge, and the message must fall outside the border rather than
 * stretch the box — set rvFieldErrorIn to a selector for the ancestor that
 * should hold it instead.
 */
@Directive({
  selector: 'input.rv-input[formControlName], select.rv-input[formControlName], textarea.rv-input[formControlName], textarea.cmf-notes-in[formControlName]',
})
export class RvFieldErrorDirective implements AfterViewInit, OnDestroy {
  /** Selector of the ancestor to hold the message; defaults to the control's wrapper. */
  @Input() rvFieldErrorIn: string = ''

  private subscriptions = new Subscription()
  private error_node: HTMLElement | null = null
  private error_host: HTMLElement | null = null

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private validation: FormValidationService,
    @Optional() private control: NgControl
  ) {}

  /** The control's key in its form group ('tank_brand'); null when the element is not bound. */
  get key(): string | null {
    return this.control?.path?.join('.') ?? null
  }

  /** The row label shown next to this control, for the editor's missing-field chips. */
  get label(): string {
    return labelFor(this.el.nativeElement)
  }

  /** The control itself: the editor tags it data-target so its chip can scroll to it. */
  get element(): HTMLElement {
    return this.el.nativeElement
  }

  // ngAfterViewInit, not ngOnInit: FormControlName binds the underlying control
  // during its own ngOnChanges, and NgControl.statusChanges returns null until
  // then — subscribing too early silently never fires, leaving the message on
  // screen after the field is filled in.
  ngAfterViewInit(): void {
    this.subscriptions.add(this.validation.show_errors$.subscribe(() => this.render()))
    // Clears the message as soon as the field is corrected (and re-adds it if
    // the value goes invalid again) while the messages are on screen.
    if (this.control?.statusChanges) {
      this.subscriptions.add(this.control.statusChanges.subscribe(() => this.render()))
    }
  }

  // Belt and braces for the typing case: statusChanges only emits when the
  // validity flips, so a field that is invalid for two reasons at once (or a
  // control whose accessor updates the model after this listener) would keep a
  // stale message. Re-rendering straight off the DOM events covers both.
  @HostListener('input')
  @HostListener('change')
  @HostListener('blur')
  onUserEdit(): void {
    // setTimeout: run after the value accessor has pushed the new value into
    // the control and validators have re-run, so errors are up to date.
    setTimeout(() => this.render())
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
    this.removeNode()
  }

  private render(): void {
    const message = this.validation.showing ? this.messageFor() : ''
    if (!message) {
      this.removeNode()
      return
    }

    if (!this.error_node) {
      const host = this.el.nativeElement
      const target = this.errorHost()
      if (!target) return

      this.error_node = this.renderer.createElement('div')
      this.renderer.addClass(this.error_node, 'rv-field-error')
      this.renderer.addClass(target, 'rv-has-error')
      this.error_host = target

      if (target === host.parentElement) {
        // insertBefore with a null reference node appends, keeping the message
        // directly after the control inside its own wrapper.
        this.renderer.insertBefore(target, this.error_node, host.nextSibling)
      } else {
        // A named container (rvFieldErrorIn): the message belongs after
        // everything already in it, e.g. below AVI's Test Result badge.
        this.renderer.appendChild(target, this.error_node)
      }
    }
    this.renderer.setProperty(this.error_node, 'textContent', message)
  }

  /** Where the message goes: the named ancestor if one was asked for, else the wrapper. */
  private errorHost(): HTMLElement | null {
    const host = this.el.nativeElement
    if (this.rvFieldErrorIn) {
      return host.closest<HTMLElement>(this.rvFieldErrorIn) ?? host.parentElement
    }
    return host.parentElement
  }

  private removeNode(): void {
    if (!this.error_node) return
    // Remove from whichever container it was added to — errorHost() could now
    // resolve differently (the badge is re-rendered as the value changes).
    if (this.error_host) {
      this.renderer.removeChild(this.error_host, this.error_node)
      this.renderer.removeClass(this.error_host, 'rv-has-error')
    }
    this.error_node = null
    this.error_host = null
  }

  private messageFor(): string {
    return controlMessage(this.control?.errors)
  }
}
